import { StateCreator } from 'zustand';

import { SbkBetSlip } from '../../types';
import { CriteriaState, arePromoCriteriaSatisfied } from './criterion-validators';

/**
 * Composes a betslip state update with promo revalidation: applied promos whose
 * criteria are no longer satisfied after the update are silently removed.
 */
export const validatePromos = <T extends Partial<SbkBetSlip>>(
    state: Pick<SbkBetSlip, 'selectedPromos'> & CriteriaState,
    update: T
): T & Partial<Pick<SbkBetSlip, 'selectedPromos'>> => {
    const criteriaState: CriteriaState = { ...state, ...update };
    const selectedPromos = update.selectedPromos ?? state.selectedPromos;

    const unsatisfiedBetIds = Object.keys(selectedPromos).filter(betId => {
        const promo = selectedPromos[betId];
        return promo && !arePromoCriteriaSatisfied(promo, criteriaState, betId);
    });
    if (unsatisfiedBetIds.length === 0) {
        return update;
    }

    const revalidatedPromos = { ...selectedPromos };
    unsatisfiedBetIds.forEach(betId => {
        revalidatedPromos[betId] = null;
    });
    return { ...update, selectedPromos: revalidatedPromos };
};

type SbkStateCreator = StateCreator<SbkBetSlip, [], []>;

/**
 * Store middleware that runs every state update through validatePromos, so no
 * action can leave an applied promo whose criteria are no longer satisfied.
 *
 * Only intercepts the `set` handed to the store's actions — direct
 * `api.setState` calls (e.g. persist rehydration) bypass it.
 */
export const withPromoValidation =
    (config: SbkStateCreator): SbkStateCreator =>
    (set, get, api) => {
        // replace=true is ignored: no caller uses it, and validated updates always merge
        const validatedSet: typeof set = partial => {
            // set() accepts either an update object or an updater function; resolve
            // to a plain object so validatePromos can inspect it
            const update = typeof partial === 'function' ? partial(get()) : partial;
            set(validatePromos(get(), update));
        };
        // give the store's actions our wrapped set instead of the real one
        return config(validatedSet, get, api);
    };
